import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Pencil, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { authApi, getImageUrl } from "../api";
import { useStore } from "../store";
import { requestNotificationPermission, showLocalNotification } from "../registerSW";
import { enablePush } from "../push";

const EMPTY_FORM = { name: "", price: "", description: "", categories: [], image_url: "", images: [], sold_out: false, best_seller: false };
const ORDER_STATUSES = ["ricevuto", "confermato", "in preparazione", "spedito", "consegnato"];

export default function AdminDashboard() {
  const { adminToken, setAdminToken, categories } = useStore();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("products");
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  const client = () => authApi(adminToken);
  const knownOrderIds = useRef(new Set());

  useEffect(() => {
    if (!adminToken) {
      navigate("/admin");
      return;
    }
    knownOrderIds.current = new Set();
    requestNotificationPermission();
    enablePush().catch(() => {});
    loadAll();
    const interval = setInterval(() => checkNewOrders(), 20000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken]);

  const loadAll = async () => {
    try {
      const [p, o] = await Promise.all([client().get("/admin/products"), client().get("/admin/orders")]);
      setProducts(p.data.products);
      setOrders(o.data.orders);
      o.data.orders.forEach((ord) => knownOrderIds.current.add(ord.id));
    } catch {
      toast.error("Sessione scaduta. Accedi di nuovo.", { position: "top-center" });
      setAdminToken(null);
      navigate("/admin");
    } finally {
      setLoading(false);
    }
  };

  const checkNewOrders = async () => {
    try {
      const { data } = await client().get("/admin/orders");
      setOrders(data.orders);
      // Notify ONLY for orders never seen before in this session
      const fresh = data.orders.filter((o) => !knownOrderIds.current.has(o.id));
      if (fresh.length) {
        fresh.forEach((o) => knownOrderIds.current.add(o.id));
        setTab("orders");
        toast.success(`Nuovo ordine ricevuto da ${fresh[0].customer_name}!`, { position: "top-center", duration: 8000 });
        showLocalNotification(
          "Dorae — Nuovo ordine",
          `Ordine ricevuto da ${fresh[0].customer_name} · € ${fresh[0].total.toFixed(2)}`,
          "/admin"
        );
      }
    } catch {
      /* polling silenzioso */
    }
  };

  const markOrderSeen = async (id) => {
    try {
      await client().post(`/admin/orders/${id}/mark-seen`);
      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, is_new: false, admin_seen: true } : o)));
    } catch {}
  };

  const newCount = orders.filter((o) => o.is_new).length;

  const uploadImage = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await client().post("/admin/upload", fd);
        setForm((f) => {
          const images = [...(f.images || []), data.url];
          return { ...f, images, image_url: f.image_url || data.url };
        });
      }
      toast.success(files.length > 1 ? "Foto caricate" : "Foto caricata", { position: "top-center" });
    } catch {
      toast.error("Caricamento non riuscito", { position: "top-center" });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (url) => {
    setForm((f) => {
      const images = (f.images || []).filter((i) => i !== url);
      return { ...f, images, image_url: f.image_url === url ? (images[0] || "") : f.image_url };
    });
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, price: parseFloat(form.price) };
      if (editingId) {
        await client().put(`/admin/products/${editingId}`, body);
        toast.success("Capo aggiornato", { position: "top-center" });
      } else {
        await client().post("/admin/products", body);
        toast.success("Capo aggiunto", { position: "top-center" });
      }
      setForm(EMPTY_FORM);
      setEditingId(null);
      loadAll();
    } catch {
      toast.error("Salvataggio non riuscito", { position: "top-center" });
    }
  };

  const removeProduct = async (id) => {
    if (!window.confirm("Eliminare questo capo?")) return;
    try {
      await client().delete(`/admin/products/${id}`);
      toast.success("Capo eliminato", { position: "top-center" });
      loadAll();
    } catch {
      toast.error("Eliminazione non riuscita", { position: "top-center" });
    }
  };

  const updateOrderStatus = async (id, status) => {
    try {
      await client().put(`/admin/orders/${id}`, { status });
      toast.success("Stato aggiornato", { position: "top-center" });
      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status, is_new: false, admin_seen: true } : o)));
    } catch {
      toast.error("Aggiornamento non riuscito", { position: "top-center" });
    }
  };

  const removeOrder = async (id) => {
    if (!window.confirm("Eliminare questo ordine?")) return;
    try {
      await client().delete(`/admin/orders/${id}`);
      toast.success("Ordine eliminato", { position: "top-center" });
      setOrders((prev) => prev.filter((o) => o.id !== id));
    } catch {
      toast.error("Eliminazione non riuscita", { position: "top-center" });
    }
  };

  const toggleCategory = (slug) => {
    setForm((f) => ({
      ...f,
      categories: f.categories.includes(slug) ? f.categories.filter((c) => c !== slug) : [...f.categories, slug],
    }));
  };

  if (loading) {
    return <div className="py-24 text-center text-sm text-[#8B8578] animate-pulse" data-testid="admin-loading">Caricamento...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 pb-24" data-testid="admin-dashboard">
      <div className="flex items-center justify-between pt-5 pb-6">
        <h1 className="font-display text-2xl text-[#2C2C2C]">Area riservata</h1>
        <button
          data-testid="admin-logout"
          onClick={() => { setAdminToken(null); navigate("/admin"); }}
          className="flex items-center gap-2 text-[12px] tracking-wide-spaced uppercase text-[#8B5A2B] hover:opacity-70 transition-opacity"
        >
          <LogOut size={15} strokeWidth={1.5} /> Esci
        </button>
      </div>

      <div className="flex gap-6 border-b border-[#EAE4DA] mb-8">
        {["products", "orders"].map((t) => (
          <button
            key={t}
            data-testid={`tab-${t}`}
            onClick={() => setTab(t)}
            className={`pb-3 text-[12px] tracking-wide-spaced uppercase transition-colors ${tab === t ? "text-[#AA7C11] border-b-2 border-[#D4AF37]" : "text-[#8B8578]"}`}
          >
            {t === "products" ? `Capi (${products.length})` : `Ordini (${orders.length})`}
            {t === "orders" && newCount > 0 && (
              <span data-testid="new-orders-badge" className="ml-2 inline-flex items-center justify-center bg-[#D4AF37] text-white text-[10px] font-semibold min-w-[17px] h-[17px] rounded-full px-1">
                {newCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === "products" && (
        <div className="grid sm:grid-cols-2 gap-10">
          {/* Form */}
          <form onSubmit={saveProduct} className="space-y-4 bg-white border border-[#EAE4DA] p-5 h-fit" data-testid="product-form">
            <h2 className="font-display text-lg text-[#2C2C2C]">{editingId ? "Modifica capo" : "Nuovo capo"}</h2>
            <input required data-testid="form-name" placeholder="Nome capo *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-[#EAE4DA] px-4 py-3 text-base outline-none focus:border-[#D4AF37]" />
            <input required type="number" step="0.01" min="0" data-testid="form-price" placeholder="Prezzo (€) *" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="w-full border border-[#EAE4DA] px-4 py-3 text-base outline-none focus:border-[#D4AF37]" />
            <textarea data-testid="form-description" placeholder="Descrizione" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border border-[#EAE4DA] px-4 py-3 text-base outline-none focus:border-[#D4AF37] resize-none" />
            <input data-testid="form-image-url" placeholder="URL immagine (o carica foto dal telefono)" value={form.image_url} onChange={(e) => { const url = e.target.value; setForm((f) => ({ ...f, image_url: url, images: url ? (f.images?.length ? f.images : [url]) : [] })); }} className="w-full border border-[#EAE4DA] px-4 py-3 text-base outline-none focus:border-[#D4AF37]" />
            <label className="flex items-center gap-3 cursor-pointer text-sm text-[#8B5A2B]" data-testid="form-upload-label">
              <Upload size={16} strokeWidth={1.5} />
              {uploading ? "Caricamento..." : "Carica foto dal telefono (anche più di una)"}
              <input type="file" accept="image/*" multiple className="hidden" onChange={uploadImage} data-testid="form-upload-input" />
            </label>
            {(form.images || []).length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="form-images-preview">
                {form.images.map((img, i) => (
                  <div key={i} className="relative">
                    <img src={getImageUrl(img)} alt="" className={`w-16 h-20 object-cover border ${i === 0 ? "border-[#D4AF37]" : "border-[#EAE4DA]"}`} />
                    {i === 0 && <span className="absolute bottom-0 left-0 right-0 bg-[#D4AF37] text-white text-[9px] text-center tracking-wide uppercase">Copertina</span>}
                    <button type="button" data-testid={`remove-img-${i}`} onClick={() => removeImage(img)} aria-label="Rimuovi foto" className="absolute -top-2 -right-2 bg-[#2C2C2C] text-white rounded-full w-5 h-5 flex items-center justify-center">
                      <X size={11} strokeWidth={2} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-3">
              <button
                type="button"
                data-testid="form-sold-out"
                onClick={() => setForm((f) => ({ ...f, sold_out: !f.sold_out }))}
                className={`flex-1 px-4 py-3 text-[11px] tracking-wide-spaced uppercase border transition-colors ${form.sold_out ? "bg-[#2C2C2C] text-white border-transparent" : "border-[#EAE4DA] text-[#6B6357]"}`}
              >
                Esaurito
              </button>
              <button
                type="button"
                data-testid="form-best-seller"
                onClick={() => setForm((f) => ({ ...f, best_seller: !f.best_seller }))}
                className={`flex-1 px-4 py-3 text-[11px] tracking-wide-spaced uppercase border transition-colors ${form.best_seller ? "gold-gradient text-white border-transparent" : "border-[#EAE4DA] text-[#6B6357]"}`}
              >
                Best seller
              </button>
            </div>
            <div>
              <p className="text-[11px] tracking-wide-spaced uppercase text-[#8B8578] mb-2">Categorie *</p>
              <div className="flex flex-wrap gap-2">
                {categories.map((c) => (
                  <button
                    type="button"
                    key={c.slug}
                    data-testid={`form-cat-${c.slug}`}
                    onClick={() => toggleCategory(c.slug)}
                    className={`px-3 py-1.5 text-[11px] tracking-wide uppercase border transition-colors ${form.categories.includes(c.slug) ? "gold-gradient text-white border-transparent" : "border-[#EAE4DA] text-[#6B6357]"}`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="submit" data-testid="form-submit" className="gold-gradient text-white px-8 py-3 text-[12px] font-semibold tracking-wide-spaced uppercase hover:opacity-90 transition-opacity">
                {editingId ? "Salva" : "Aggiungi"}
              </button>
              {editingId && (
                <button type="button" data-testid="form-cancel" onClick={() => { setEditingId(null); setForm(EMPTY_FORM); }} className="px-6 py-3 text-[12px] tracking-wide-spaced uppercase text-[#8B8578] border border-[#EAE4DA]">
                  Annulla
                </button>
              )}
            </div>
          </form>

          {/* List */}
          <div className="space-y-4" data-testid="admin-product-list">
            {products.map((p) => (
              <div key={p.id} className="flex gap-4 bg-white border border-[#EAE4DA] p-3 items-center" data-testid={`admin-product-${p.id}`}>
                <img src={getImageUrl(p.images?.length ? p.images[0] : p.image_url)} alt={p.name} className="w-14 h-[70px] object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="font-display text-sm text-[#2C2C2C] truncate">{p.name}</p>
                  <p className="text-xs text-[#8B8578] mt-0.5">€ {p.price.toFixed(2)} · {p.categories.join(", ")}</p>
                </div>
                <button data-testid={`edit-${p.id}`} onClick={() => { setEditingId(p.id); setForm({ name: p.name, price: p.price, description: p.description, categories: p.categories, image_url: p.image_url || "", images: p.images?.length ? p.images : (p.image_url ? [p.image_url] : []), sold_out: !!p.sold_out, best_seller: !!p.best_seller }); }} className="p-2 text-[#8B5A2B]" aria-label="Modifica">
                  <Pencil size={16} strokeWidth={1.5} />
                </button>
                <button data-testid={`delete-${p.id}`} onClick={() => removeProduct(p.id)} className="p-2 text-[#B0A898] hover:text-red-500" aria-label="Elimina">
                  <Trash2 size={16} strokeWidth={1.5} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="space-y-4" data-testid="admin-order-list">
          {!orders.length && <p className="text-center text-sm text-[#8B8578] py-14">Nessun ordine ricevuto.</p>}
          {orders.map((o) => (
            <div key={o.id} className={`relative bg-white border p-5 ${o.is_new ? "border-[#D4AF37]" : "border-[#EAE4DA]"}`} data-testid={`admin-order-${o.id}`}>
              {o.is_new && (
                <span data-testid={`new-order-tag-${o.id}`} className="absolute -top-2.5 left-4 gold-gradient text-white text-[9px] font-semibold tracking-wide-spaced uppercase px-3 py-1">
                  Nuovo
                </span>
              )}
              <div className="flex justify-between items-start gap-3">
                <div className="min-w-0">
                  <p className="font-display text-base text-[#2C2C2C]">{o.customer_name}</p>
                  <p className="text-xs text-[#8B8578] mt-0.5">{o.customer_email} · {o.customer_phone}</p>
                  <p className="text-xs text-[#8B8578] mt-0.5">{o.address}, {o.city} {o.zip_code}</p>
                </div>
                <p className="text-sm font-semibold gold-gradient-text whitespace-nowrap">€ {o.total.toFixed(2)}</p>
              </div>
              <ul className="mt-3 pt-3 border-t border-[#EDE6DB] space-y-1">
                {o.items.map((i) => (
                  <li key={i.product_id} className="text-xs text-[#6B6357] flex justify-between">
                    <span>{i.quantity} × {i.name}</span>
                    <span>€ {(i.price * i.quantity).toFixed(2)}</span>
                  </li>
                ))}
              </ul>
              {o.notes && <p className="mt-2 text-xs italic text-[#8B8578]">“{o.notes}”</p>}
              <div className="mt-4 pt-3 border-t border-[#EDE6DB] flex items-center justify-between gap-3">
                <select
                  data-testid={`order-status-${o.id}`}
                  value={o.status}
                  onFocus={() => markOrderSeen(o.id)}
                  onChange={(e) => { updateOrderStatus(o.id, e.target.value); markOrderSeen(o.id); }}
                  className="border border-[#EAE4DA] bg-white px-3 py-2 text-[11px] tracking-wide uppercase outline-none focus:border-[#D4AF37]"
                >
                  {ORDER_STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <div className="flex items-center gap-1">
                  {o.is_new && (
                    <button data-testid={`seen-order-${o.id}`} onClick={() => markOrderSeen(o.id)} className="text-[11px] tracking-wide uppercase text-[#AA7C11] px-2 py-2 hover:opacity-70 transition-opacity">
                      Segna letto
                    </button>
                  )}
                  <button data-testid={`delete-order-${o.id}`} onClick={() => removeOrder(o.id)} className="p-2 text-[#B0A898] hover:text-red-500" aria-label="Elimina ordine">
                    <Trash2 size={16} strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
